import { useState, useEffect, useMemo, useRef } from "react";
import config from "../config.js";
import { getRightToLeftBusIcon } from "../pages/stops/vehicleIcons.js";

/**
 * Custom hook for managing vehicle and stop icons across maps
 * Handles S3 icon fetching, colorization, and fallback to defaults
 *
 * @param {Object} settings - Settings object containing icon paths
 * @param {Array} vehicles - Array of vehicle objects
 * @param {Array} stops - Array of stop objects
 * @param {Array} routes - Array of route objects
 * @returns {Object} - Icon getter functions and loading state
 */
export const useMapIcons = (
  settings,
  vehicles = [],
  stops = [],
  routes = [],
) => {
  const [colorizedVehicleIcons, setColorizedVehicleIcons] = useState({});
  const [isLoadingIcons, setIsLoadingIcons] = useState(false);
  const deviceIdToVehicleColor = useRef({});
  const colorizedVehicleIconsRef = useRef({});
  const vehicleColorKey = useMemo(
    () =>
      vehicles
        .map((vehicle) => {
          const deviceId = vehicle?.device_id || "";
          const color = vehicle?.vehicle_color || vehicle?.color || "green";
          return `${deviceId}:${color}`;
        })
        .join("|"),
    [vehicles],
  );

  // Populate device ID to vehicle color mapping
  useEffect(() => {
    const colorMap = {};
    vehicles.forEach((vehicle) => {
      colorMap[vehicle.device_id] =
        vehicle.vehicle_color || vehicle.color || "green";
    });
    deviceIdToVehicleColor.current = colorMap;
  }, [vehicles]);

  useEffect(() => {
    colorizedVehicleIconsRef.current = colorizedVehicleIcons;
  }, [colorizedVehicleIcons]);

  // Fetch and colorize S3 vehicle icons
  useEffect(() => {
    const loadColorizedIcons = async () => {
      if (!settings?.vehicleIcons) {
        setColorizedVehicleIcons((prev) =>
          Object.keys(prev).length ? {} : prev,
        );
        return;
      }

      setIsLoadingIcons(true);
      const newColorizedIcons = {};

      try {
        for (const vehicle of vehicles) {
          const deviceId = vehicle.device_id;
          const color = vehicle.vehicle_color || vehicle.color || "blue";
          const cacheKey = `${deviceId}-${color}`;

          // Check if we already have this colorized version
          if (!colorizedVehicleIconsRef.current[cacheKey]) {
            const colorizedSVG = await fetchAndColorizeSVG(
              settings.vehicleIcons,
              color,
            );
            if (colorizedSVG) {
              newColorizedIcons[cacheKey] = colorizedSVG;
            }
          }
        }

        if (Object.keys(newColorizedIcons).length > 0) {
          setColorizedVehicleIcons((prev) => ({
            ...prev,
            ...newColorizedIcons,
          }));
        }
      } catch (error) {
        console.error("Error loading vehicle icons:", error);
      } finally {
        setIsLoadingIcons(false);
      }
    };

    loadColorizedIcons();
  }, [settings?.vehicleIcons, vehicleColorKey]);

  /**
   * Fetch and colorize SVG from S3
   */
  const fetchAndColorizeSVG = async (iconPath, color) => {
    try {
      const response = await fetch(`${config.baseURL}/icon/file/${iconPath}`);
      const svgText = await response.text();

      // Replace ${color} placeholders directly in the SVG text
      const colorizedSVG = svgText.replace(/\$\{color\}/g, color);

      return colorizedSVG;
    } catch (error) {
      console.error("Error fetching/colorizing SVG:", error);
      return null;
    }
  };

  /**
   * Get vehicle icon with proper rotation and direction
   *
   * @param {string} deviceId - Device ID of the vehicle
   * @param {number} heading - Current heading/bearing of vehicle
   * @param {string} direction - Direction: 'source_to_dest' or 'dest_to_source'
   * @returns {Object} - Google Maps icon object
   */
  /**
   * Compute icon pixel size based on current map zoom level.
   * At zoom >= 16 → full size (45×44); shrinks linearly down to 20×20 at zoom 10.
   */
  const getIconSize = (zoom) => {
    const MAX_ZOOM = 16;
    const MIN_ZOOM = 10;
    const MAX_W = 72; const MAX_H = 72;
    const MIN_W = 36; const MIN_H = 36;
    if (zoom === null || zoom === undefined || zoom >= MAX_ZOOM) {
      return { w: MAX_W, h: MAX_H };
    }
    if (zoom <= MIN_ZOOM) {
      return { w: MIN_W, h: MIN_H };
    }
    const t = (zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM); // 0..1
    return {
      w: Math.round(MIN_W + t * (MAX_W - MIN_W)),
      h: Math.round(MIN_H + t * (MAX_H - MIN_H)),
    };
  };

  const buildDefaultVehicleIcon = (color, heading, w, h) => {
    const rotatedSVG = getRightToLeftBusIcon(color, heading).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(Math.round(w / 2), Math.round(h / 2)),
    };
  };

  const getVehicleIcon = (deviceId, heading = 0, direction = "unknown", zoom = null) => {
    const color = deviceIdToVehicleColor.current[deviceId] || "green";

    // Use the OLD WORKING rotation logic
    const adjustedHeading =
      direction !== "source_to_dest" ? (heading + 180) % 360 : heading;
    // const finalHeading = adjustedHeading + 90; // Match old code
    const finalHeading = heading + 90;

    const { w, h } = getIconSize(zoom);

    if (settings?.vehicleIcons) {
      const cacheKey = `${deviceId}-${color}`;
      const colorizedSVG = colorizedVehicleIcons[cacheKey];

      if (colorizedSVG) {
        // Apply rotation to custom SVG
        const rotationValue = finalHeading + 180; // For SVG internal rotation
        const rotatedSVG = colorizedSVG
          .replace(
            /\$\{Math\.round\(adjustedHeading\)\}/g,
            Math.round(rotationValue),
          )
          .replace(/\$\{adjustedHeading\}/g, rotationValue);

        const encodedSVG = encodeURIComponent(rotatedSVG.trim());

        return {
          url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
          scaledSize: new window.google.maps.Size(w, h),
          anchor: new window.google.maps.Point(Math.round(w / 2), Math.round(h / 2)),
        };
      }

      // Avoid flashing the raw uploaded SVG when colorization is still loading,
      // because many uploaded base icons are plain white.
      return buildDefaultVehicleIcon(color, finalHeading, w, h);
    }

    return buildDefaultVehicleIcon(color, finalHeading, w, h);
  };

  /**
   * Get origin stop icon
   *
   * @returns {Object|null} - Google Maps icon object or null for default
   */
  const getOriginStopIcon = () => {
    if (settings?.stopOriginIcons) {
      return {
        url: `${config.baseURL}/icon/file/${settings.stopOriginIcons}`,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40),
      };
    }
    return null;
  };

  /**
   * Get destination stop icon
   *
   * @returns {Object|null} - Google Maps icon object or null for default
   */
  const getDestinationStopIcon = () => {
    if (settings?.stopDestinationIcons) {
      return {
        url: `${config.baseURL}/icon/file/${settings.stopDestinationIcons}`,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40),
      };
    }
    return null;
  };

  /**
   * Get intermediate stop icon (main stop icon)
   *
   * @returns {Object|null} - Google Maps icon object or null for default
   */
  const getIntermediateStopIcon = () => {
    if (settings?.intermediateStopsIcons) {
      return {
        url: `${config.baseURL}/icon/file/${settings.intermediateStopsIcons}`,
        scaledSize: new window.google.maps.Size(32, 40),
        anchor: new window.google.maps.Point(16, 40),
      };
    }
    return null;
  };

  /**
   * Get charging point icon
   *
   * @returns {Object|null} - Google Maps icon object or null for default
   */
  const getChargingPointIcon = () => {
    if (settings?.chargingPointIcons) {
      return {
        url: `${config.baseURL}/icon/file/${settings.chargingPointIcons}`,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40),
      };
    }
    return null;
  };

  /**
   * Get railway station icon
   *
   * @returns {Object|null} - Google Maps icon object or null for default
   */
  const getRailwayStationIcon = () => {
    if (settings?.railwayStationIcons) {
      return {
        url: `${config.baseURL}/icon/file/${settings.railwayStationIcons}`,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40),
      };
    }
    return null;
  };

  /**
   * Get stop icon based on stop type and position in route
   * This is a convenience function that determines the type automatically
   *
   * @param {Object} stop - Stop object
   * @param {Array} routes - Routes array
   * @returns {Object|null} - Google Maps icon object or null for default
   */
  const getStopIcon = (stop, routes) => {
    // Determine stop type based on route position
    const route = routes.find((r) =>
      r.routeCoordinates?.some((coord) => coord.stopId === stop.id),
    );

    if (!route) return getIntermediateStopIcon();

    const coords = route.routeCoordinates;
    const stopIndex = coords.findIndex((coord) => coord.stopId === stop.id);

    // Origin (first stop)
    if (stopIndex === 0) return getOriginStopIcon();

    // Destination (last stop)
    if (stopIndex === coords.length - 1) return getDestinationStopIcon();

    // Check for special stop types
    if (stop.type === "charging_point") return getChargingPointIcon();
    if (stop.type === "railway_station") return getRailwayStationIcon();

    // Default to intermediate stop icon
    return getIntermediateStopIcon();
  };

  return {
    getVehicleIcon,
    getStopIcon,
    getOriginStopIcon,
    getDestinationStopIcon,
    getIntermediateStopIcon,
    getChargingPointIcon,
    getRailwayStationIcon,
    isLoadingIcons,
    colorizedVehicleIcons,
  };
};
